import {
  SelectionControlsCoreSize,
  SelectionControlsCoreWeight,
} from 'omakase/SelectionControls/RadioButton';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import _ from 'lodash';

const radioDimension = 24;
const radioDimensionPx = `${radioDimension}px`;

const focusOutlineThicknessPx = '2px';
const focusOutlineDistancePx = '1px';

const LABEL_TOP_SPACING = 20;

// Returns the checkmark svg using only url-safe characters, and condensing whitespace
// Really there are fewer interventions we can take on the underlying svg, but we
// don't actually need to optimize for minimum encoding size or legibility here.
//
// In earlier builds, the only thing we needed to escape was the figureColor assignment,
// but current emotion seems to get tripped up by apparent < and > characters in css strings.
// It's safe and easy just to encode broadly.
const checkmarkSvg = _.memoize((figureColor: string) =>
  encodeURIComponent(
    `<svg
      width="${radioDimensionPx}"
      height="${radioDimensionPx}"
      viewBox="0 0 ${radioDimension} ${radioDimension}"
      xmlns="http://www.w3.org/2000/svg"
      stroke="transparent"
      fill="${figureColor}"
    >
      <path d="M10 16.5L5 11.54L6.59 10L10 13.35L17.41 6L19 7.58L10 16.5Z" />
    </svg>`.replace(/\s+/g, ' ')
  )
);

const radioSvg = _.memoize((figureColor: string) =>
  encodeURIComponent(
    `<svg
      width="${radioDimensionPx}"
      height="${radioDimensionPx}"
      viewBox="0 0 ${radioDimension} ${radioDimension}"
      xmlns="http://www.w3.org/2000/svg"
      stroke="transparent"
      fill="${figureColor}"
    >
      <circle cx="6" cy="6" r="6"/>
    </svg>`.replace(/\s+/g, ' ')
  )
);

export const getTypographyFromSizeAndWeight = (
  { typography: { variant } }: Theme,
  weight: SelectionControlsCoreWeight,
  size: SelectionControlsCoreSize
) => {
  switch (weight) {
    case 'book':
      return variant(size === 'large' ? 'body01' : 'body02');
    case 'medium':
      return variant(size === 'large' ? 'body01Strong' : 'body02Strong');
    default:
      assertNever(weight);
  }
};

export const muiInputIconStyles = (theme: Theme) => css`
  content: '';
`;

const sharedBase = ({ shadow, colors }: Theme) => css`
  background: none !important;
  height: ${radioDimension}px;
  width: ${radioDimension}px;
  padding: 0;
  & input {
    appearance: none;
    box-sizing: border-box;
    opacity: 1;
    outline: none;
    position: relative;
    height: ${radioDimension}px;
    width: ${radioDimension}px;
    background: ${colors.bgDefault};
    box-shadow: ${shadow[100]};
    border: 1px solid ${colors.borderDark};
    border-radius: ${radioDimension}px;
    &::after {
      bottom: calc(-1 * (${focusOutlineThicknessPx} + ${focusOutlineDistancePx}));
      box-shadow: 0 0 0 ${focusOutlineThicknessPx} transparent;
      content: '';
      left: calc(-1 * (${focusOutlineThicknessPx} + ${focusOutlineDistancePx}));
      pointer-events: none;
      position: absolute;
      right: calc(-1 * (${focusOutlineThicknessPx} + ${focusOutlineDistancePx}));
      top: calc(-1 * (${focusOutlineThicknessPx} + ${focusOutlineDistancePx}));
      border-radius: 24px;
    }
  }
`;

// NOTE: These static colors will be replaced with an implementation that looks like our Bento checkbox component in the future and these will be deleted.
export const radioBase = (theme: Theme) => {
  const { boxShadows, colors, interactiveStates } = theme;
  return [
    sharedBase(theme),
    css`
      & input {
        ${interactiveStates.checked(css`
          // #2A2AE9 = fgPrimaryBase
          background:
            url('data:image/svg+xml,${radioSvg('#2A2AE9')}') left 5px top 5px,
            ${colors.bgDefault};
          border-color: ${colors.borderPrimaryBase};
        `)}
        ${interactiveStates.hover(css`
          background: ${colors.bgLighter};
          &:checked {
            // #0F0FC0 = fgPrimaryDark
            background:
              url('data:image/svg+xml,${radioSvg('#0F0FC0')}') left 5px top 5px,
              ${colors.bgDefault};
            border-color: ${colors.borderPrimaryDark};
          }
        `)}
      ${interactiveStates.active(css`
          background: ${colors.bgLight};
          &:checked {
            // #0F0FC0 = fgPrimaryDark
            background:
              url('data:image/svg+xml,${radioSvg('#0F0FC0')}') left 5px top 5px,
              ${colors.bgDefault};
            border-color: ${colors.borderPrimaryDark};
          }
        `)}
      ${interactiveStates.focus(css`
          &::after {
            box-shadow: 0 0 0 ${focusOutlineThicknessPx} ${colors.borderPrimaryBase};
          }
        `)}
      ${interactiveStates.disabled(css`
          border-color: ${colors.borderDefault};
          box-shadow: ${boxShadows.dropShadowNone};
          &:checked {
            // #B6B6B6 = fgDisabled
            background:
              url('data:image/svg+xml,${radioSvg('#B6B6B6')}') left 5px top 5px,
              ${colors.bgDefault};
            border-color: ${colors.borderDefault};
          }
        `)}
      }
    `,
  ];
};

export const checkboxBase = (theme: Theme) => {
  const { boxShadows, colors, interactiveStates } = theme;
  return [
    sharedBase(theme),
    css`
      & input {
        border-radius: 2px;
        &:after {
          border-radius: 2px;
        }
        ${interactiveStates.checked(css`
          // #FFFFFF = fgContrast
          background: url('data:image/svg+xml,${checkmarkSvg('#FFFFFF')}') left -1px top ${colors.fgPrimaryBase};
          border-color: ${colors.borderPrimaryBase};
        `)}
        ${interactiveStates.hover(css`
          background: ${colors.bgLighter};
          &:checked {
            // #FFFFFF = fgContrast
            background:
              url('data:image/svg+xml,${checkmarkSvg('#FFFFFF')}') left -1px top,
              ${colors.bgPrimaryDark};
            border-color: ${colors.borderPrimaryDark};
          }
        `)}
    ${interactiveStates.active(css`
          background: ${colors.bgLight};
          &:checked {
            // #FFFFFF = fgContrast
            background:
              url('data:image/svg+xml,${checkmarkSvg('#FFFFFF')}') left -1px top,
              ${colors.borderPrimaryDark};
            border-color: ${colors.borderPrimaryDark};
          }
        `)}
    ${interactiveStates.focus(css`
          &::after {
            box-shadow: 0 0 0 ${focusOutlineThicknessPx} ${colors.borderPrimaryBase};
          }
        `)}
    ${interactiveStates.disabled(css`
          border-color: ${colors.borderDefault};
          box-shadow: ${boxShadows.dropShadowNone};
          &:checked {
            // #FFFFFF = fgContrast
            background:
              url('data:image/svg+xml,${checkmarkSvg('#FFFFFF')}') left -1px top,
              ${colors.borderDefault};
            border-color: ${colors.borderDefault};
          }
        `)}
      }
    `,
  ];
};

export const formControlWrapperContainer = css`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;

  & > * + * {
    margin-top: ${LABEL_TOP_SPACING}px;
  }
`;

export const formControlLabel = ({ colors, spacing }: Theme) => css`
  align-items: flex-start;
  display: flex;
  flex: 1;
  margin: ${LABEL_TOP_SPACING}px 0 0;

  & > .MuiFormControlLabel-label {
    display: flex;
    flex-direction: column;
    margin-left: ${spacing(1.5)}px;
  }
  & > .Mui-disabled > * {
    color: ${colors.fgDisabled};
  }
  & > .MuiFormControlLabel-label.Mui-disabled {
    color: ${colors.fgDisabled};
  }
`;

export const formControlLabelPrimary = (
  theme: Theme,
  weight: SelectionControlsCoreWeight,
  size: SelectionControlsCoreSize
) => [
  getTypographyFromSizeAndWeight(theme, weight, size),
  css`
    color: ${theme.colors.fgDefault};
    display: inline-block;
    margin-top: ${size === 'small' ? 3 : 2}px;
  `,
];

export const formControlLabelHelperText = (theme: Theme, { error }: { error: boolean }) => {
  return css`
    color: ${error ? theme.colors.fgDangerBase : theme.colors.fgSubtle};
    display: inline-block;
    margin-top: 4px;

    ${theme.typography.variant('caption')};

    &.Mui-disabled {
      color: ${theme.colors.fgDisabled};
    }

    &.MuiFormHelperText-root.Mui-error {
      color: ${error ? theme.colors.fgDangerBase : theme.colors.fgSubtle};
    }
  `;
};
